/**
 * API Client for Citas Monitor Backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export interface MonitorStatus {
  is_running: boolean
  target_url: string
  seed_url: string
  last_check: string | null
  last_hash: string | null
  checks_count: number
  appointments_found: number
  uptime_seconds: number | null
}

export interface HistoryRecord {
  id: number
  timestamp: string
  appointments_found: boolean
  times_count: number
  keywords_count: number
  content_hash: string
  target_url: string
}

export interface Stats {
  total_checks: number
  appointments_found: number
  checks_last_24h: number
  success_rate_percent: number
  uptime_seconds: number | null
  is_running: boolean
}

export interface Screenshot {
  filename: string
  path: string
  size_bytes: number
  modified: string
}

export interface SchedulePolicy {
  normal_interval_min: number
  normal_interval_max: number
  critical_interval_min: number
  critical_interval_max: number
  critical_start_hour_local: number
  critical_end_hour_local: number
  timezone: string
}

export interface SchedulePolicyResponse {
  policy: SchedulePolicy
  critical_start_hour_utc: number
  critical_end_hour_utc: number
  estimated_checks_per_hour_normal: number
  estimated_checks_per_hour_critical: number
  current_window: 'normal' | 'critical'
}

export interface DetectionConfig {
  times_threshold: number
  keywords: string[]
  auto_rediscover: boolean
  rediscover_fails: number
  rediscover_backoff_sec: number
}

export interface FullConfig {
  target_url: string
  seed_url: string
  schedule: SchedulePolicy
  detection: DetectionConfig
  telegram_chat_id: string | null
  telegram_dev_chat_id: string | null
}

export interface Recommendation {
  type: 'info' | 'warning' | 'error'
  parameter: string
  message: string
  suggestion: string
}

export interface BanStatus {
  is_banned: boolean
  ban_type: string | null
  backoff_active: boolean
  backoff_remaining_seconds: number
}

export interface ConfigResponse {
  config: FullConfig
  recommendations: Recommendation[]
  current_window: 'normal' | 'critical'
  ban_status: BanStatus
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }

    return response.json()
  }

  // Status endpoints
  async getStatus(): Promise<MonitorStatus> {
    return this.fetch<MonitorStatus>('/api/status')
  }

  async startMonitor(): Promise<{ status: string; timestamp: string }> {
    return this.fetch('/api/start', { method: 'POST' })
  }

  async stopMonitor(): Promise<{ status: string; timestamp: string }> {
    return this.fetch('/api/stop', { method: 'POST' })
  }

  async checkNow(): Promise<any> {
    return this.fetch('/api/check-now', { method: 'POST' })
  }

  async updateConfig(config: {
    target_url?: string
    seed_url?: string
    check_min?: number
    check_max?: number
    telegram_chat_id?: string
  }): Promise<{ status: string; fields: string[]; timestamp: string }> {
    return this.fetch('/api/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  }

  // History & Stats
  async getHistory(limit = 100, appointmentsOnly = false): Promise<HistoryRecord[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      appointments_only: appointmentsOnly.toString(),
    })
    return this.fetch<HistoryRecord[]>(`/api/history?${params}`)
  }

  async getStats(): Promise<Stats> {
    return this.fetch<Stats>('/api/stats')
  }

  async getScreenshots(limit = 20): Promise<{ screenshots: Screenshot[] }> {
    const params = new URLSearchParams({ limit: limit.toString() })
    return this.fetch<{ screenshots: Screenshot[] }>(`/api/screenshots?${params}`)
  }

  // Schedule Policy
  async getSchedulePolicy(): Promise<SchedulePolicyResponse> {
    return this.fetch<SchedulePolicyResponse>('/api/schedule-policy')
  }

  async updateSchedulePolicy(policy: SchedulePolicy): Promise<SchedulePolicyResponse> {
    return this.fetch<SchedulePolicyResponse>('/api/schedule-policy', {
      method: 'PUT',
      body: JSON.stringify(policy),
    })
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; monitor_running: boolean }> {
    return this.fetch('/health')
  }

  // Full Configuration
  async getFullConfig(): Promise<ConfigResponse> {
    return this.fetch<ConfigResponse>('/api/full-config')
  }

  async updateFullConfig(config: FullConfig): Promise<ConfigResponse> {
    return this.fetch<ConfigResponse>('/api/full-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  }

  // Ban Status
  async getBanStatus(): Promise<BanStatus> {
    return this.fetch<BanStatus>('/api/ban-status')
  }
}

export const api = new ApiClient(API_BASE_URL)
